/**
 * AnimationPromptReasoningService
 *
 * Uses Gemini with thinking to refine user animation prompts before
 * sending them to Veo for video generation. The system prompt rules
 * and user's animation description are analyzed and compressed into
 * a Veo-optimized prompt.
 */

import { GoogleGenAI } from '@google/genai';
import type { SlotScreenId } from '../../../core/types/slot-screen-generator.types';
import { useSlotAssetGeneratorStore } from '@store/slotAssetGeneratorStore';
import { resolveApiKey } from '@utils/api-keys';
import { shouldUseProxy, proxyGenerateContent } from '../ai-proxy';

const REASONING_MODEL = 'gemini-3-flash-preview';

const META_SYSTEM_INSTRUCTION = `You are an animation prompt engineer specializing in AI video generation with Google Veo.
Your task is to take a user's animation description and a system prompt containing animation rules, then produce a single, refined animation prompt optimized for Veo image-to-video generation.

Rules:
- The output must be a single prompt string — no markdown, no explanation, no headers, no bullet points.
- Incorporate all relevant rules from the system prompt (timing, visual rules, transitions).
- Preserve the user's creative intent while adding technical specificity.
- Include camera motion, timing, particle effects, and transition details.
- Keep the prompt under 500 words for Veo compatibility.
- The prompt should describe what happens visually in the 8-second clip.
- Write in plain descriptive English as continuous prose.`;

function buildUserMessage(screenName: string, animationSystemPrompt: string, userAnimationPrompt: string): string {
  return `Screen: ${screenName}

--- System Animation Rules ---
${animationSystemPrompt}

--- User's Animation Description ---
${userAnimationPrompt}

Produce a refined Veo animation prompt for this screen.`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractTextAndThinking(response: any): { refinedPrompt: string; reasoning: string } {
  let refinedPrompt = '';
  let reasoning = '';
  const candidate = response.candidates?.[0];
  if (!candidate?.content?.parts) return { refinedPrompt, reasoning };

  for (const part of candidate.content.parts) {
    if (part.thought && part.text) {
      reasoning += part.text;
      continue;
    }
    if (part.text && !part.thought) {
      refinedPrompt += part.text;
    }
  }

  return { refinedPrompt: refinedPrompt.trim(), reasoning: reasoning.trim() };
}

/**
 * Refine an animation prompt using Gemini reasoning.
 * Combines the per-screen system prompt with the user's animation description
 * and produces a Veo-optimized prompt.
 */
export async function refineAnimationPrompt(
  _screenId: SlotScreenId,
  userAnimationPrompt: string,
  animationSystemPrompt: string,
  screenName: string,
  signal?: AbortSignal,
): Promise<{ refinedPrompt: string; reasoning: string }> {
  const contents = [{
    role: 'user' as const,
    parts: [{ text: buildUserMessage(screenName, animationSystemPrompt, userAnimationPrompt) }],
  }];

  const config = {
    thinkingConfig: {
      includeThoughts: true,
      thinkingBudget: 4096,
    },
  };

  const systemInstruction = {
    parts: [{ text: META_SYSTEM_INSTRUCTION }],
  };

  if (shouldUseProxy()) {
    const response = await proxyGenerateContent(
      { model: REASONING_MODEL, contents, config, systemInstruction },
      signal,
    );
    return extractTextAndThinking(response);
  }

  // Direct SDK fallback with user's own key
  const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
  const apiKey = resolveApiKey('google', apiKeys.google);
  if (!apiKey) {
    throw new Error('Google API key required for animation prompt reasoning.');
  }

  const ai = new GoogleGenAI({ apiKey });
  const response = await ai.models.generateContent({
    model: REASONING_MODEL,
    contents,
    config,
    systemInstruction,
  });

  return extractTextAndThinking(response);
}
